/**
 * Shared "the backend is temporarily unable to answer" handling for every backend command,
 * regardless of transport. `~/lib/tauriInvoke.ts` routes every `invoke()` through
 * `resilientInvoke` below, so nothing here needs repeating per call site:
 *
 * - **Classification.** Overload (`RESOURCE_EXHAUSTED` / HTTP 429 / HTTP 503), restarts
 *   (`UNAVAILABLE`) and timeouts (`DEADLINE_EXCEEDED` / HTTP 504) are all the same thing to the
 *   user — "try again shortly" — and never an auth failure. Under Tauri only an error *string*
 *   crosses the IPC bridge, so Rust tags these with a `[transient:<CODE>]` prefix (see
 *   `commands::grpc_error` in `src-tauri/src/commands/mod.rs`); the browser build's
 *   `RestApiError` carries the HTTP status (and `Retry-After`) directly.
 * - **Retry.** Only commands listed in `READ_COMMANDS` / `IDEMPOTENT_WRITE_COMMANDS` are repeated
 *   automatically. Anything else (notably every create/join command) is *not*: on a timeout the
 *   request may already have been applied, so the caller shows the error and re-reads the
 *   affected list instead.
 * - **In-flight dedup.** The server runs one user's Users requests and one group's Groups
 *   requests strictly one after another, so a duplicate of a request that's already on the wire
 *   is never faster — it only eats into the per-user concurrency limit. Identical concurrent
 *   calls to a `DEDUPED_COMMANDS` command share one promise.
 */

export const SERVICE_UNAVAILABLE_MESSAGE = "Сервис временно недоступен, попробуйте ещё раз"

/** Backoff before each automatic retry; ±30% jitter is applied on top (see `backoffDelayMs`). */
const RETRY_DELAYS_MS = [1000, 2000, 4000]

/**
 * Pure reads — repeating one can't change anything server-side. Commands absent from both lists
 * are never retried automatically.
 */
const READ_COMMANDS = new Set([
  // users
  "get_me",
  // groups
  "get_user_groups",
  "get_group",
  "get_group_users",
  "get_group_roles",
  "get_group_invites",
  "get_group_bans",
  "get_channel_permission_overwrites",
  "get_category_permission_overwrites",
  "get_group_notification_settings",
  // messages
  "get_channel_messages",
  "get_message",
  "get_messages_around",
  // friends
  "get_friends",
  "get_incoming_requests",
  "get_outgoing_requests",
  "get_blocked_users",
  "get_friendship_status",
  // subscriptions
  "get_subscription_plans",
  "get_my_subscription",
  "get_subscription_payment",
  "get_ai_models",
  "get_ai_usage",
  "get_notification_settings",
  // attachments
  "get_attachment",
  "get_download_url",
  // bots / interactions / agent credentials
  "get_my_bots",
  "get_bot",
  "get_available_intents",
  "get_bot_commands",
  "get_group_commands",
  "get_agent_credential",
  // agent tasks
  "get_agent_task_settings",
  "list_agent_tasks",
  "get_agent_task",
  "get_agent_task_runs",
  // boards
  "get_board",
  "list_board_cards",
  "get_board_card",
  "list_board_assignees",
  "list_my_board_accounts",
])

/**
 * Writes that set a value rather than create something, so applying one twice ends in the same
 * state. Create/join commands (`create_group`, `create_invite`, `create_role`, `create_category`,
 * `create_channel`, `join_by_invite`, ...) must never be added here.
 */
const IDEMPOTENT_WRITE_COMMANDS = new Set([
  "update_user",
  "update_user_status",
  "set_group_mute",
  "update_group_notification_settings",
  "update_group",
  "set_channel_permission_overwrite",
  "delete_channel_permission_overwrite",
  "set_category_permission_overwrite",
  "delete_category_permission_overwrite",
  "set_notification_settings",
  "set_agent_task_state",
  "move_board_card",
  "set_board_card_assignees",
])

/**
 * Reads whose result is only ever assigned/merged into store state, never mutated in place by the
 * caller — so handing the same result object to two concurrent callers is safe. Deliberately
 * narrower than `READ_COMMANDS`.
 */
const DEDUPED_COMMANDS = new Set([
  "get_me",
  "get_user_groups",
  "get_group",
  "get_group_users",
  "get_group_roles",
  "get_group_invites",
  "get_group_bans",
])

export function isReadCommand(cmd: string): boolean {
  return READ_COMMANDS.has(cmd)
}

/**
 * What callers see once a transient failure is final (retries used up, or the command isn't
 * retryable). `message` is ready to show to the user as-is; the server's own text is kept in
 * `serverMessage` for logs.
 */
export class ServiceUnavailableError extends Error {
  /** `RESOURCE_EXHAUSTED` | `UNAVAILABLE` | `DEADLINE_EXCEEDED` | `HTTP_<status>`. */
  readonly code: string
  readonly serverMessage: string

  constructor(code: string, serverMessage: string) {
    super(SERVICE_UNAVAILABLE_MESSAGE)
    this.name = "ServiceUnavailableError"
    this.code = code
    this.serverMessage = serverMessage
  }
}

export function isServiceUnavailableError(error: unknown): error is ServiceUnavailableError {
  return error instanceof ServiceUnavailableError
}

interface TransientFailure {
  code: string
  serverMessage: string
  /** From a REST `Retry-After` header, when the server sent one. */
  retryAfterMs?: number
}

const TRANSIENT_PREFIX = /^\[transient:([A-Z_]+)\]\s*([\s\S]*)$/
const TRANSIENT_HTTP_STATUSES = new Set([429, 503, 504])
// `google.rpc.Code` values as they appear in a transcoded REST error body's `code` field.
const TRANSIENT_GRPC_CODES: Record<number, string> = {
  4: "DEADLINE_EXCEEDED",
  8: "RESOURCE_EXHAUSTED",
  14: "UNAVAILABLE",
}

/** Returns what kind of transient failure `error` is, or null for any other error. */
export function classifyTransient(error: unknown): TransientFailure | null {
  if (error instanceof ServiceUnavailableError) {
    return { code: error.code, serverMessage: error.serverMessage }
  }

  // Browser build: `RestApiError` — duck-typed so this module doesn't depend on `restClient.ts`
  // (which itself uses `retryTransient` for the token refresh).
  if (error instanceof Error && typeof (error as { status?: unknown }).status === "number") {
    const rest = error as Error & { status: number; body?: { code?: number; message?: string }; retryAfterMs?: number }
    const grpcCode = rest.body?.code !== undefined ? TRANSIENT_GRPC_CODES[rest.body.code] : undefined
    if (grpcCode || TRANSIENT_HTTP_STATUSES.has(rest.status)) {
      return {
        code: grpcCode ?? `HTTP_${rest.status}`,
        serverMessage: rest.body?.message ?? rest.message,
        retryAfterMs: rest.retryAfterMs,
      }
    }
    return null
  }

  // Tauri build: a rejected command arrives as the bare error string.
  const message = typeof error === "string" ? error : error instanceof Error ? error.message : ""
  const match = TRANSIENT_PREFIX.exec(message)
  return match ? { code: match[1]!, serverMessage: match[2]! } : null
}

function backoffDelayMs(attempt: number, retryAfterMs?: number): number {
  const jittered = RETRY_DELAYS_MS[attempt]! * (0.7 + Math.random() * 0.6)
  // A server-provided `Retry-After` is a floor, never shortened by jitter.
  return Math.max(jittered, retryAfterMs ?? 0)
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Runs `call`, repeating it after a backoff for as long as it fails transiently and retries
 * remain. A transient failure that's final is rethrown as `ServiceUnavailableError`; every other
 * error passes through untouched.
 */
export async function retryTransient<T>(label: string, call: () => Promise<T>, retry = true): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await call()
    } catch (error) {
      const transient = classifyTransient(error)
      if (!transient) throw error
      if (!retry || attempt >= RETRY_DELAYS_MS.length) {
        console.warn(`[retry] ${label} failed (${transient.code}): ${transient.serverMessage}`)
        throw new ServiceUnavailableError(transient.code, transient.serverMessage)
      }
      const delay = backoffDelayMs(attempt, transient.retryAfterMs)
      console.warn(
        `[retry] ${label} failed (${transient.code}), retry ${attempt + 1}/${RETRY_DELAYS_MS.length} in ${Math.round(delay)}ms`,
      )
      await sleep(delay)
    }
  }
}

const inFlight = new Map<string, Promise<unknown>>()

/** `call` with this module's retry and in-flight dedup policy for `cmd` applied. */
export function resilientInvoke<T>(cmd: string, args: Record<string, unknown> | undefined, call: () => Promise<T>): Promise<T> {
  const retryable = READ_COMMANDS.has(cmd) || IDEMPOTENT_WRITE_COMMANDS.has(cmd)
  if (!DEDUPED_COMMANDS.has(cmd)) return retryTransient(cmd, call, retryable)

  const key = `${cmd}:${JSON.stringify(args ?? {})}`
  const existing = inFlight.get(key)
  if (existing) return existing as Promise<T>

  const request = retryTransient(cmd, call, retryable).finally(() => inFlight.delete(key))
  inFlight.set(key, request)
  return request
}
